import Button from '@/shared/Button'
import FormItem from '@/shared/FormCreator/FormItem'
import Close from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { Box, Grid, IconButton, Stack, Typography } from '@mui/material'

import { yupResolver } from '@hookform/resolvers/yup'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import unionBy from 'lodash/unionBy'
import moment from 'moment'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import * as Yup from 'yup'

import debounce from '@mui/material/utils/debounce'

import SendingTime from '@/components/PhishCampaignEmail/SendingTime'

import { v4 as uuidv4 } from 'uuid'
import Tooltip from '@/shared/Tooltip'
import combineDateTime from '../libs/utils/combineDateTime'

const inputConfig = {
  inputBaseProps: {
    alignItems: 'center',
    childrenSx: {
      '& .MuiOutlinedInput-root': {
        background: 'white',
      },
    },
  },
}

const btnProps = {
  size: 'small',
  sx: { borderRadius: 2, padding: 0, color: theme => theme.palette.secondary_80.main },
}

const mapStateToProps = ({ loading }) => ({
  isUserGroupLoading: loading['userGroups/getUserGroups'],
  isEmailTemplateLabelsLoading: loading['emailTemplate/getEmailTemplateLabels'],
  isLandingPageLabelsLoading: loading['landingPage/getLandingPageLabels'],
})

const MAX_FIELDS = 50

const PhishCampaignCreateGroupItem = forwardRef(
  (
    {
      multi = false,
      dispatch,
      index,
      isEmailTemplateLabelsLoading,
      isLandingPageLabelsLoading,
      isUserGroupLoading,
      onDelete,
      setPreviewEmailTemplateId,
      updateSendingTimes,
      sendingTimes,
    },
    ref,
  ) => {
    const { t } = useTranslation()

    // sending time component
    const handleAddSendingTime = () => {
      if (sendingTimes.length < MAX_FIELDS) {
        const newSendingTimes = [...sendingTimes, { id: uuidv4(), dateTime: new Date() }]
        updateSendingTimes(index, newSendingTimes)
      }
    }

    // sending time component
    const handleModifySendingTime = useCallback(
      (id, updatedTime) => {
        const newSendingTimes = sendingTimes.map(times =>
          times.id === id ? { ...times, ...updatedTime } : times,
        )
        updateSendingTimes(index, newSendingTimes)
      },
      [index, sendingTimes, updateSendingTimes],
    )

    // sending time component
    const handleDeleteSendingTime = useCallback(
      (id, deleteAll) => {
        if (deleteAll) {
          const newSendingTimes = sendingTimes.filter(time => time.id === id)
          updateSendingTimes(index, newSendingTimes)
        } else {
          const newSendingTimes = sendingTimes.filter(time => time.id !== id)
          updateSendingTimes(index, newSendingTimes)
        }
      },
      [index, sendingTimes, updateSendingTimes],
    )

    const validations = {
      userGroup: Yup.object()
        .required(t('validation.user_group_required'))
        .typeError(t('validation.user_group_required')),
      startDate: Yup.date().minStartDate(
        t('validation.min_startdate'),
        t('validation.min_startdate2'),
      ),
      startTime: Yup.date().typeError('').minStartDate('', ''),
      endDate: Yup.date().afterStartDate(t('validation.after_startdate')),
      endTime: Yup.date().typeError('').afterStartDate(''),
      emailTemplate: Yup.object()
        .nullable()
        .emailTemplate(t('validation.email_template_required'))
        .typeError(t('validation.email_template_required')),
      emailTemplateLabel: Yup.object()
        .nullable()
        .emailTemplateLabel(t('validation.email_template_label_required'))
        .when('phishType', {
          is: val => val === 'T',
          then: schema => schema.required(t('validation.email_template_label_required')),
          otherwise: schema => schema.nullable(),
        })
        .typeError(t('validation.email_template_label_required')),
      landingPage: Yup.object()
        .nullable()
        .landingPage(t('validation.landing_page_required'))
        .typeError(t('validation.landing_page_required')),
    }

    if (multi) {
      validations.name = Yup.string().required(t('validation.campaign_name_required'))
    }

    const form = useForm({
      resolver: yupResolver(Yup.object().shape(validations)),
    })

    const userGroupsSearchInputRef = useRef()
    const emailTemplateLabelsSearchInputRef = useRef()
    const emailTemplatesSearchInputRef = useRef()
    const landingPageLabelsSearchInputRef = useRef()
    const landingPagesSearchInputRef = useRef()

    const [userGroupsSearchInput, setUserGroupsSearchInput] = useState('')
    const [userGroupsSearchResults, setUserGroupsSearchResults] = useState({})
    const [userGroupsPage, setUserGroupsPage] = useState(0)

    const [emailTemplatesSearchInput, setEmailTemplatesSearchInput] = useState('')
    const [emailTemplatesSearchResults, setEmailTemplatesSearchResults] = useState({})
    const [emailTemplatesPage, setEmailTemplatesPage] = useState(0)

    const [landingPagesSearchInput, setLandingPagesSearchInput] = useState('')
    const [landingPagesSearchResults, setLandingPagesSearchResults] = useState({})
    const [landingPagesPage, setLandingPagesPage] = useState(0)

    const [emailTemplateLabelsSearchInput, setEmailTemplateLabelsSearchInput] = useState('')
    const [emailTemplateLabelsSearchResults, setEmailTemplateLabelsSearchResults] = useState({})
    const [emailTemplateLabelsPage, setEmailTemplateLabelsPage] = useState(0)

    const [landingPageLabelsSearchInput, setLandingPageLabelsSearchInput] = useState('')
    const [landingPageLabelsSearchResults, setLandingPageLabelsSearchResults] = useState({})
    const [landingPageLabelsPage, setLandingPageLabelsPage] = useState(0)

    const [eventStart, setEventStart] = useState()
    const [eventEnd, setEventEnd] = useState()

    // const [emailTemplatePreview, setEmailTemplatePreview] = useState()
    // const [landingPagePreview, setLandingPagePreview] = useState()
    // const [showLandingPagePreview, setShowLandingPagePreview] = useState(false)

    const {
      control,
      watch,
      setValue,
      // eslint-disable-next-line no-unused-vars
      formState: { errors },
    } = form

    useImperativeHandle(ref, () => ({
      form,
    }))

    const isStartTimeFixed = isEqual(watch('dateRangeType'), '1')
    const isSendingDateRangeType = isEqual(watch('sendingDateRangeType'), '2')

    const phishType = watch('phishType')
    const phishEmailLabelType = watch('phishEmailLabelType')
    const landingType = watch('landingType')
    const emailTemplate = watch('emailTemplate')
    const emailTemplateLabel = watch('emailTemplateLabel')
    const landingPage = watch('landingPage')
    const landingPageLabel = watch('landingPageLabel')

    const isPhishRandom = phishType === 'R' || phishType === 'T'
    const isLandingDefault = landingType === 'default'
    const startDate = watch('startDate')
    const startTime = watch('startTime')
    const endDate = watch('endDate')
    const endTime = watch('endTime')

    const { data: userGroupsSearchResultsData = [], paging: userGroupsSearchResultsPaging } =
      userGroupsSearchResults

    const {
      data: emailTemplatesSearchResultsData = [],
      paging: emailTemplatesSearchResultsPaging,
    } = emailTemplatesSearchResults

    const { data: landingPagesSearchResultsData = [], paging: landingPagesSearchResultsPaging } =
      landingPagesSearchResults

    const {
      data: emailTemplateLabelsSearchResultsData = [],
      paging: emailTemplateLabelsSearchResultsPaging,
    } = emailTemplateLabelsSearchResults

    const {
      data: landingPageLabelsSearchResultsData = [],
      paging: landingPageLabelsSearchResultsPaging,
    } = landingPageLabelsSearchResults

    // fetch user groups START
    const fetchUserGroups = useCallback(() => {
      dispatch({
        type: 'userGroup/getUserGroups',
        payload: {
          pageIndex: userGroupsPage,
          pageSize: 100,
          searchGroupName: userGroupsSearchInputRef.current,
        },
        successCallback: response => {
          const { data: responseData = [], paging: responsePaging = {} } = response || {}
          const { pageIndex } = responsePaging

          const newData = responseData.map(d => ({ id: d.groupId, label: d.groupName }))

          setUserGroupsSearchResults(s => ({
            data: pageIndex > 0 ? unionBy(s.data || [], newData, 'id') : newData,
            paging: responsePaging,
          }))
        },
      })
    }, [dispatch, userGroupsPage])

    const loadMoreUserGroupsDebounce = debounce(() => {
      setUserGroupsPage(p => p + 1)
    }, 400)

    const fetchUserGroupsDebounce = useMemo(
      () =>
        debounce(() => {
          fetchUserGroups()
        }, 400),
      [fetchUserGroups],
    )

    useEffect(() => {
      fetchUserGroupsDebounce()
    }, [userGroupsSearchInput, userGroupsPage, fetchUserGroupsDebounce])
    // fetch user groups END

    // fetch email template labels START
    const fetchEmailTemplateLabels = useCallback(() => {
      dispatch({
        type: 'emailTemplate/getEmailTemplateLabels',
        payload: {
          type: isPhishRandom ? phishEmailLabelType : phishType,
          searchLabelName: emailTemplateLabelsSearchInputRef.current,
          pageIndex: emailTemplateLabelsPage,
          pageSize: 100,
        },
        successCallback: response => {
          const { data: responseData, paging: responsePaging } = response
          const { pageIndex } = responsePaging

          const newData = responseData.map(d => ({ id: d.labelId, label: d.labelName }))

          setEmailTemplateLabelsSearchResults(s => ({
            data: pageIndex > 0 ? unionBy(s.data || [], newData, 'id') : newData,
            paging: responsePaging,
          }))
        },
      })
    }, [dispatch, phishType, phishEmailLabelType, isPhishRandom, emailTemplateLabelsPage])

    const loadMoreEmailTemplateLabelsPagesDebounce = debounce(() => {
      setEmailTemplateLabelsPage(p => p + 1)
    }, 400)

    const fetchEmailTemplateLabelsDebounce = useMemo(
      () =>
        debounce(() => {
          fetchEmailTemplateLabels()
        }, 400),
      [fetchEmailTemplateLabels],
    )

    useEffect(() => {
      fetchEmailTemplateLabelsDebounce()
    }, [emailTemplateLabelsSearchInput, fetchEmailTemplateLabelsDebounce, emailTemplateLabelsPage])
    // fetch email template labels END

    // fetch email templates START
    const fetchEmailTemplates = useCallback(() => {
      dispatch({
        type: 'emailTemplate/getEmailTemplates',
        payload: {
          pageIndex: emailTemplatesPage,
          pageSize: 100,
          searchParam: emailTemplatesSearchInputRef.current,
          sort: 'EMAIL_TEMP_NAME_ASC',
          type: isPhishRandom ? phishEmailLabelType : phishType,
          labelId: emailTemplateLabel?.id,
        },
        successCallback: response => {
          const { data: responseData = [], paging: responsePaging = {} } = response || {}
          const { pageIndex } = responsePaging

          const newData = responseData.map(d => ({ id: d.emailTempId, label: d.name, data: d }))

          setEmailTemplatesSearchResults(s => ({
            data: pageIndex > 0 ? unionBy(s.data || [], newData, 'id') : newData,
            paging: responsePaging,
          }))
        },
      })
    }, [
      dispatch,
      emailTemplatesPage,
      phishType,
      phishEmailLabelType,
      emailTemplateLabel,
      isPhishRandom,
    ])

    const loadMoreEmailTemplatesDebounce = debounce(() => {
      setEmailTemplatesPage(p => p + 1)
    }, 400)

    const fetchEmailTemplatesDebounce = useMemo(
      () =>
        debounce(() => {
          fetchEmailTemplates()
        }, 400),
      [fetchEmailTemplates],
    )

    useEffect(() => {
      fetchEmailTemplatesDebounce()
    }, [emailTemplatesSearchInput, emailTemplatesPage, fetchEmailTemplatesDebounce])
    // fetch email templates END

    // email templates clean up START
    useEffect(() => {
      setEmailTemplatesSearchResults({})
      setEmailTemplatesPage(0)

      setEmailTemplateLabelsSearchResults({})
      setEmailTemplateLabelsPage(0)

      setEmailTemplatesSearchInput('')
      setEmailTemplateLabelsSearchInput('')

      emailTemplatesSearchInputRef.current = ''
      emailTemplateLabelsSearchInputRef.current = ''
      setValue('emailTemplateLabel', '')
      setValue('emailTemplate', '')
    }, [phishType, phishEmailLabelType, setValue])
    // email templates clean up END

    // fetch landing page labels START
    const fetchLandingPageLabels = useCallback(() => {
      if (!isLandingDefault) {
        dispatch({
          type: 'landingPage/getLandingPageLabels',
          payload: {
            type: landingType,
            pageIndex: landingPageLabelsPage,
            pageSize: 100,
            searchLabelName: landingPageLabelsSearchInputRef.current,
          },
          successCallback: response => {
            const { data: responseData, paging: responsePaging } = response
            const { pageIndex } = responsePaging

            const newData = responseData.map(d => ({
              id: d.landingpageLabelId,
              label: d.labelName,
            }))

            setLandingPageLabelsSearchResults(s => ({
              data: pageIndex > 0 ? unionBy(s.data || [], newData, 'id') : newData,
              paging: responsePaging,
            }))
          },
        })
      }
    }, [dispatch, landingType, isLandingDefault, landingPageLabelsPage])

    const loadMoreLandingPageLabelsPagesDebounce = debounce(() => {
      setLandingPageLabelsPage(p => p + 1)
    }, 400)

    const fetchLandingPageLabelsDebounce = useMemo(
      () =>
        debounce(() => {
          fetchLandingPageLabels()
        }, 400),
      [fetchLandingPageLabels],
    )

    useEffect(() => {
      fetchLandingPageLabelsDebounce()
    }, [landingPageLabelsSearchInput, fetchLandingPageLabelsDebounce, landingPageLabelsPage])
    // fetch landing page labels END

    // fetch landing pages START
    const fetchLandingPages = useCallback(() => {
      if (!isLandingDefault) {
        dispatch({
          type: 'landingPage/getLandingPages',
          payload: {
            pageIndex: landingPagesPage,
            pageSize: 100,
            searchParam: landingPagesSearchInputRef.current,
            sort: 'LANDINGPAGE_NAME_ASC',
            type: landingType,
            labelId: landingPageLabel?.id,
          },
          successCallback: response => {
            const { data: responseData = [], paging: responsePaging = {} } = response || {}
            const { pageIndex } = responsePaging

            const newData = responseData.map(d => ({
              id: d.landingpageTempId,
              label: d.name,
              data: d,
            }))

            setLandingPagesSearchResults(s => ({
              data: pageIndex > 0 ? unionBy(s.data || [], newData, 'id') : newData,
              paging: responsePaging,
            }))
          },
        })
      }
    }, [dispatch, landingPagesPage, landingType, landingPageLabel, isLandingDefault])

    const loadMoreLandingPagesDebounce = debounce(() => {
      setLandingPagesPage(p => p + 1)
    }, 400)

    const fetchLandingPagesDebounce = useMemo(
      () =>
        debounce(() => {
          fetchLandingPages()
        }, 400),
      [fetchLandingPages],
    )

    useEffect(() => {
      fetchLandingPagesDebounce()
    }, [landingPagesSearchInput, landingPagesPage, fetchLandingPagesDebounce])
    // fetch landing pages END

    // landing page clean up START
    useEffect(() => {
      setLandingPagesSearchResults({})
      setLandingPagesPage(0)

      setLandingPageLabelsSearchResults({})
      setLandingPageLabelsPage(0)

      setLandingPagesSearchInput('')
      setLandingPageLabelsSearchInput('')

      landingPagesSearchInputRef.current = ''
      landingPageLabelsSearchInputRef.current = ''

      setValue('landingPageLabel', '')
      setValue('landingPage', '')
    }, [landingType, setValue])
    // landing page clean up END

    const refreshNowTime = () => {
      setValue?.('startDate', moment().toDate())
      setValue?.('startTime', moment().toDate())
    }

    useEffect(() => {
      if (isStartTimeFixed) {
        setValue?.('startDate', moment().toDate())
        setValue?.('startTime', moment().toDate())

        setValue?.('endDate', moment().add(1, 'days').toDate())
        setValue?.('endTime', moment().add(1, 'days').toDate())
      } else {
        setValue?.('startDate', moment().add(15, 'minutes').toDate())
        setValue?.('startTime', moment().add(15, 'minutes').toDate())

        setValue?.('endDate', moment().add(15, 'minutes').add(1, 'days').toDate())
        setValue?.('endTime', moment().add(15, 'minutes').add(1, 'days').toDate())
      }
    }, [isStartTimeFixed, setValue])

    useEffect(() => {
      const start = combineDateTime(startDate, startTime)
      const end = combineDateTime(endDate, endTime)
      if (eventStart === undefined) {
        setEventStart(start)
      }
      if (eventEnd === undefined) {
        setEventEnd(end)
      }
      if (eventStart !== undefined || eventEnd !== undefined) {
        if (!moment(eventStart).startOf('minutes').isSame(moment(start).startOf('minutes'))) {
          setEventStart(start)
        }
        if (!moment(eventEnd).startOf('minutes').isSame(moment(end).startOf('minutes'))) {
          setEventEnd(end)
        }
      }
    }, [startDate, startTime, endDate, endTime, eventStart, eventEnd])

    return (
      <Grid
        container
        spacing={4}
        sx={{
          position: 'relative',
          background: multi && index % 2 === 0 ? '#2D2D6C1A' : 'transparent',
          p: 3,
          mt: -1,
          pb: 5,
        }}
      >
        {index >= 2 && (
          <Box sx={{ position: 'absolute', top: 30, right: 20 }}>
            <IconButton
              onClick={() => {
                onDelete?.()
              }}
            >
              <Close />
            </IconButton>
          </Box>
        )}
        {multi && (
          <Grid item xs={12} sm={6}>
            <FormItem
              label={t('general.child_campaign_name')}
              defaultValue=""
              name="name"
              control={control}
              inputConfig={{ ...inputConfig, inputProps: { maxLength: 30 } }}
              errors={errors}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={multi ? 6 : 12}>
          <FormItem
            label={t('general.select_group')}
            name="userGroup"
            type="autocomplete"
            defaultValue=""
            control={control}
            errors={errors}
            inputConfig={{
              ...inputConfig,
              onInputChange: (_, value, reason) => {
                userGroupsSearchInputRef.current = value

                if (['input', 'clear'].includes(reason)) {
                  setUserGroupsPage(0)
                  setUserGroupsSearchInput(value)
                }
              },
              onFocus: () => {
                fetchUserGroupsDebounce()
              },
              onLoadMore: () => {
                const { hasNext } = userGroupsSearchResultsPaging

                if (!isUserGroupLoading && hasNext) {
                  loadMoreUserGroupsDebounce()
                }
              },
              options: userGroupsSearchResultsData ?? null,
              filterOptions: opts => opts,
              isOptionEqualToValue: (option, value) => option.id === value.id ?? false,
              renderOption: (renderProps, option) => (
                <Box component="li" {...renderProps} key={option.id}>
                  {option.label}
                </Box>
              ),
            }}
          />
        </Grid>

        {/* second part for event start and end time */}
        <Grid item xs={12}>
          <Stack direction={'row'} spacing={2} alignItems={'center'}>
            <FormItem
              defaultValue={2}
              label={t('general.event_time')}
              name="dateRangeType"
              type="radio"
              control={control}
              inputConfig={{
                options: [
                  { value: 1, label: t('general.now', { num: 1 }) },
                  { value: 2, label: t('general.custom_time', { num: 2 }) },
                ],
              }}
              errors={errors}
            />

            <FormItem
              disabled={isStartTimeFixed}
              defaultValue={moment().startOf('day').toDate()}
              name="startDate"
              type="date"
              control={control}
              inputConfig={{
                ...inputConfig,
                sx: {
                  width: 180,
                  '& .MuiFormHelperText-root.Mui-error': {
                    wordBreak: 'keep-all',
                    whiteSpace: 'nowrap',
                  },
                },
              }}
              errors={errors}
            />

            <FormItem
              disabled={isStartTimeFixed}
              defaultValue={new Date()}
              name="startTime"
              type="time"
              control={control}
              inputConfig={{
                ...inputConfig,
                sx: { width: 160 },
              }}
              errors={errors}
            />

            {/* add a refresh button for step now too long use, only renew start time */}
            {isStartTimeFixed && (
              <IconButton
                disabled={!isStartTimeFixed}
                {...btnProps}
                size="small"
                paddingleft={2}
                sx={{ color: '#2D2D6C' }}
                onClick={() => {
                  refreshNowTime()
                }}
              >
                <RefreshIcon />
              </IconButton>
            )}

            <Typography>~</Typography>

            <FormItem
              defaultValue={moment().startOf('day').toDate()}
              name="endDate"
              type="date"
              control={control}
              inputConfig={{
                ...inputConfig,
                sx: {
                  width: 180,
                  '& .MuiFormHelperText-root.Mui-error': { wordBreak: 'keep-all' },
                  whiteSpace: 'nowrap',
                },
              }}
              errors={errors}
            />

            <FormItem
              defaultValue={new Date()}
              name="endTime"
              type="time"
              control={control}
              inputConfig={{
                ...inputConfig,
                sx: { width: 160 },
              }}
              errors={errors}
            />
          </Stack>
        </Grid>

        {/* third part that is select sending time */}
        <Grid item xs={12} md={12}>
          <Grid>
            <Stack direction={'row'} spacing={2} alignItems={'center'}>
              <FormItem
                defaultValue={1}
                label={t('general.schedule_model')}
                name="sendingDateRangeType"
                type="radio"
                control={control}
                inputConfig={{
                  options: [
                    { value: 1, label: t('general.default', { num: 1 }) },
                    { value: 2, label: t('general.average', { num: 2 }) },
                  ],
                }}
                errors={errors}
              />
              <Tooltip title={t('general.send_time_max')} placement="top-start">
                <span>
                  <IconButton
                    disabled={!isSendingDateRangeType}
                    size="small"
                    onClick={handleAddSendingTime}
                    color="secondary_80"
                  >
                    <AddCircleOutlineIcon fontSize="small" color="secondary_80" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Grid>
          <Grid item container spacing={1} columnSpacing={{ xs: 1, sm: 1, md: 3 }} marginTop={2}>
            {sendingTimes.map((objs, i) => (
              <Grid
                key={`sendTime_${i + 1}`}
                rowSpacing={2}
                marginTop={2}
                style={{ justifyContent: 'space-around', alignItems: 'baseline' }}
              >
                <SendingTime
                  name="sendingTime"
                  key={`sendTime_${i + 1}`}
                  field={objs.id}
                  onDelete={(uuid, deleteAll) => handleDeleteSendingTime(uuid, deleteAll)}
                  onChange={(uuid, updatedTime) => handleModifySendingTime(uuid, updatedTime)}
                  disabled={!isSendingDateRangeType}
                  eventStart={eventStart}
                  eventEnd={eventEnd}
                ></SendingTime>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* fourth part that is choose email temp and replace new one email temp */}
        <Grid item xs={12} md={6}>
          <FormItem
            defaultValue={'T'}
            label={t('general.select_phish_mail')}
            name="phishType"
            type="radio"
            control={control}
            inputConfig={{
              ...inputConfig,
              onChange: val => {
                if (get(val, 'target.value', '') === 'T' || get(val, 'target.value', '') === 'R') {
                  setValue?.('landingType', 'default')
                }
              },
              options: [
                { value: 'T', label: t('general.total') },
                { value: 'R', label: t('general.random') },
                { value: 'S', label: t('menu.system') },
                { value: 'I', label: t('menu.individual') },
              ],
            }}
            errors={errors}
          />

          <Stack direction="row" spacing={1} mt={2}>
            {isPhishRandom && (
              <FormItem
                defaultValue="S"
                name="phishEmailLabelType"
                type="select"
                control={control}
                inputConfig={{
                  ...inputConfig,
                  sx: { width: 120 },
                  options: [
                    { value: 'S', label: t('menu.system') },
                    { value: 'I', label: t('menu.individual') },
                  ],
                }}
                errors={errors}
              />
            )}

            <FormItem
              name="emailTemplateLabel"
              type="autocomplete"
              defaultValue=""
              control={control}
              errors={errors}
              inputConfig={{
                ...inputConfig,
                placeholder: t('general.select_label'),
                clearOnBlur: true,
                onInputChange: (_, value) => {
                  emailTemplateLabelsSearchInputRef.current = value
                  setEmailTemplateLabelsSearchInput(value)
                },
                onFocus: () => {
                  fetchEmailTemplateLabelsDebounce()
                },
                onLoadMore: () => {
                  const { hasNext } = emailTemplateLabelsSearchResultsPaging

                  if (!isEmailTemplateLabelsLoading && hasNext) {
                    loadMoreEmailTemplateLabelsPagesDebounce()
                  }
                },
                options: emailTemplateLabelsSearchResultsData,
                filterOptions: opts => opts,
                isOptionEqualToValue: (option, value) => option.id === value.id,
                renderOption: (renderProps, option) => (
                  <Box component="li" {...renderProps} key={option.id}>
                    {option.label}
                  </Box>
                ),
              }}
            />

            {!isPhishRandom && (
              <FormItem
                name="emailTemplate"
                type="autocomplete"
                defaultValue=""
                control={control}
                errors={errors}
                inputConfig={{
                  ...inputConfig,
                  placeholder: t('general.select_template'),
                  onInputChange: (_, value, reason) => {
                    emailTemplatesSearchInputRef.current = value

                    if (['input', 'clear'].includes(reason)) {
                      setEmailTemplatesPage(0)
                      setEmailTemplatesSearchInput(value)
                    }
                  },
                  onFocus: () => {
                    fetchEmailTemplatesDebounce()
                  },
                  onLoadMore: () => {
                    const { hasNext } = emailTemplatesSearchResultsPaging

                    if (!isUserGroupLoading && hasNext) {
                      loadMoreEmailTemplatesDebounce()
                    }
                  },
                  options: emailTemplatesSearchResultsData,
                  filterOptions: opts => opts,
                  isOptionEqualToValue: (option, value) => option.id === value.id,
                  renderOption: (renderProps, option) => (
                    <Box component="li" {...renderProps} key={option.id}>
                      {option.label}
                    </Box>
                  ),
                }}
              />
            )}

            {!isPhishRandom && (
              <Button
                disabled={!emailTemplate}
                color="secondary_80"
                variant="outlined"
                onClick={() => {
                  setPreviewEmailTemplateId(emailTemplate?.id)
                }}
              >
                {t('general.preview')}
              </Button>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormItem
            defaultValue={'default'}
            label={t('general.select_landing_page')}
            name="landingType"
            type="radio"
            control={control}
            inputConfig={{
              ...inputConfig,
              options: [
                { value: 'default', label: t('general.preset'), disabled: isPhishRandom },
                { value: 'S', label: t('menu.system'), disabled: isPhishRandom },
                { value: 'I', label: t('menu.individual'), disabled: isPhishRandom },
              ],
            }}
            errors={errors}
          />

          {!isPhishRandom && (
            <Stack direction="row" spacing={1} mt={2}>
              {isLandingDefault ? (
                <FormItem
                  defaultValue=""
                  disabled={true}
                  name="defaultLanding"
                  control={control}
                  inputConfig={{ ...inputConfig, inputProps: { maxLength: 40 } }}
                  errors={errors}
                />
              ) : (
                <>
                  <FormItem
                    name="landingPageLabel"
                    type="autocomplete"
                    defaultValue=""
                    control={control}
                    errors={errors}
                    inputConfig={{
                      ...inputConfig,
                      placeholder: t('general.select_label'),
                      clearOnBlur: true,
                      onInputChange: (_, value) => {
                        landingPageLabelsSearchInputRef.current = value
                        setLandingPageLabelsSearchInput(value)
                      },
                      onFocus: () => {
                        fetchLandingPageLabelsDebounce()
                      },
                      onLoadMore: () => {
                        const { hasNext } = landingPageLabelsSearchResultsPaging

                        if (!isLandingPageLabelsLoading && hasNext) {
                          loadMoreLandingPageLabelsPagesDebounce()
                        }
                      },
                      options: landingPageLabelsSearchResultsData,
                      filterOptions: opts => opts,
                      isOptionEqualToValue: (option, value) => option.id === value.id,
                      renderOption: (renderProps, option) => (
                        <Box component="li" {...renderProps} key={option.id}>
                          {option.label}
                        </Box>
                      ),
                    }}
                  />

                  <FormItem
                    name="landingPage"
                    type="autocomplete"
                    defaultValue=""
                    control={control}
                    errors={errors}
                    inputConfig={{
                      ...inputConfig,
                      placeholder: t('general.select_template'),
                      onInputChange: (_, value, reason) => {
                        landingPagesSearchInputRef.current = value

                        if (['input', 'clear'].includes(reason)) {
                          setLandingPagesPage(0)
                          setLandingPagesSearchInput(value)
                        }
                      },
                      onFocus: () => {
                        fetchLandingPagesDebounce()
                      },
                      onLoadMore: () => {
                        const { hasNext } = landingPagesSearchResultsPaging

                        if (!isUserGroupLoading && hasNext) {
                          loadMoreLandingPagesDebounce()
                        }
                      },
                      options: landingPagesSearchResultsData,
                      filterOptions: opts => opts,
                      isOptionEqualToValue: (option, value) => option.id === value.id,
                      renderOption: (renderProps, option) => (
                        <Box component="li" {...renderProps} key={option.id}>
                          {option.label}
                        </Box>
                      ),
                    }}
                  />
                </>
              )}
              <Button
                disabled={!landingPage}
                color="secondary_80"
                variant="outlined"
                onClick={() => {
                  dispatch({
                    type: 'landingPage/getLandingPageHtml',
                    payload: { landingpageTempId: landingPage.data.landingpageTempId },
                    successCallback: html => {
                      const w = window.open('', '_blank', '') // access the "about:blank" window you've opened
                      w.document.body.innerHTML = html
                    },
                  })
                }}
              >
                {t('general.preview')}
              </Button>
            </Stack>
          )}
        </Grid>
      </Grid>
    )
  },
)

export default connect(mapStateToProps, null, null, { forwardRef: true })(
  PhishCampaignCreateGroupItem,
)
